import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Checkbox, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { money } from '../../../utils/salaryFormulas'
import { ddmm } from '../../../utils/periodDates'
import { useAppSelector } from '../../../app/hooks'
import { SUPER_ADMIN } from '../../../utils/constants'
import { MentorSalaryRow } from '../../../features/salary/salarySlice'
import { directorApi, SalaryDecision } from '../../../features/director/directorApi'
import { downloadFile } from '../../school/schoolStudentsPage/downloadFile'
import { HeadFilters, useHeadFilters, useHeadIndex } from '../../head/headData'
import { useDirectorSubject } from '../../head/headScope'
import { Pill, PctPill, bodyRow, btn, headRow, muted, page, sub, tableWrap } from '../ui'
import RemindersPanel from './RemindersPanel'
import HeadSalariesTab from './HeadSalariesTab'
import PeriodsTab from './PeriodsTab'

const TABS = ['mentors', 'heads', 'periods'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = { mentors: 'salaryMentors', heads: 'dirHeadsTab', periods: 'dirPeriodsTab' }

export const surveyEntered = (m: { surveyFilled?: boolean; surveyPercent?: number | null }) =>
    !!m.surveyFilled || (m.surveyPercent != null && m.surveyPercent >= 0)

const DirectorSalaryPage = () => {
    const { formatMessage } = useIntl()
    const [params, setParams] = useSearchParams()
    const tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'mentors'

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'headSalary' })}</H3>
                <Box sx={{ ...sub, margin: '6px 0 16px', maxWidth: 760 }}>{formatMessage({ id: `dirSalarySub_${tab}` })}</Box>

                {tab === 'mentors' && <RemindersPanel />}

                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginBottom: '16px' }}>
                    {TABS.map((t) => (
                        <Box key={t} sx={tabSx(t === tab)} onClick={() => setParams({ tab: t }, { replace: true })}>
                            {formatMessage({ id: TAB_LABEL[t] })}
                        </Box>
                    ))}
                </Box>

                {tab === 'mentors' && <MentorsTab />}
                {tab === 'heads' && <HeadSalariesTab />}
                {tab === 'periods' && <PeriodsTab />}
            </Box>
        </Box>
    )
}

const MentorsTab = () => {
    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const f = useHeadFilters()
    const index = useHeadIndex()
    const { subjectId } = useDirectorSubject()
    const isSuper = useAppSelector((s) => s.user.userInfo.roles.includes(SUPER_ADMIN))
    const month = f.monthNumber
    const week = f.weekNumber || 0
    const isWeek = week > 0

    const { data, isFetching } = directorApi.useGetSalaryQuery(
        { courseId: f.courseId || 0, subjectId: subjectId || 0, monthNumber: month || 0, weekNumber: week },
        { skip: !month },
    )
    const [decide, { isLoading: deciding }] = directorApi.useDecideMutation()
    const rows = useMemo(
        () => (data?.mentors ?? []).filter((m) => !f.groupId || m.groupId === f.groupId),
        [data, f.groupId],
    )
    const name = (m: MentorSalaryRow) => `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || `#${m.mentorUserId}`

    const act = async (m: MentorSalaryRow, decision: SalaryDecision) => {
        try {
            await decide({ mentorUserId: m.mentorUserId, courseSubjectId: m.courseSubjectId, cycleNumber: month!, decision }).unwrap()
            toast.success(formatMessage({ id: `dirDecision_${decision}` }))
        } catch { /* общий обработчик */ }
    }

    const open = (m: MentorSalaryRow) =>
        navigate(
            `/director/salary/${m.mentorUserId}?courseSubjectId=${m.courseSubjectId}&cycleNumber=${month}&weekNumber=${week}` +
                (m.groupId ? `&groupId=${m.groupId}` : ''),
        )

    const onExcel = async () => {
        const qs = new URLSearchParams({ courseId: String(f.courseId || 0), subjectId: String(subjectId || 0), monthNumber: String(month || 0) })
        const ok = await downloadFile(`director/salary/export.xlsx?${qs}`, `mentor-salary-${month}-month.xlsx`)
        if (!ok) toast.error(formatMessage({ id: 'salaryExportFail' }))
    }

    const total = rows.reduce((s, m) => s + (m.total || 0), 0)
    const stop = (e: React.SyntheticEvent) => e.stopPropagation()

    return (
        <>
            <HeadFilters filters={f} index={index}>
                {!isWeek && (
                    <Button variant="outlined" sx={{ ...btn, alignSelf: 'flex-end' }} disabled={!month} onClick={onExcel} title={formatMessage({ id: 'dirExcelHint' })}>
                        ⬇ {formatMessage({ id: 'dirExcel' })}
                    </Button>
                )}
            </HeadFilters>

            {!month ? (
                <Box sx={{ ...sub, padding: '24px 0' }}>{formatMessage({ id: 'headPickMonth' })}</Box>
            ) : (
                <>
                    {isFetching && <LinearProgress />}
                    {isWeek && <Box sx={{ ...muted, marginBottom: '8px' }}>{formatMessage({ id: 'dirWeekNote' })}</Box>}
                    <Box sx={tableWrap}>
                        <Table sx={{ minWidth: isWeek ? 860 : 1120 }}>
                            <TableHead sx={headRow}>
                                <TableRow>
                                    <TableCell>{formatMessage({ id: isWeek ? 'colMentor' : 'dirMentorGroup' })}</TableCell>
                                    <TableCell align="right">{formatMessage({ id: 'colStudents' })}</TableCell>
                                    <TableCell align="center">{formatMessage({ id: 'dirVideo' })}</TableCell>
                                    <TableCell align="center">{formatMessage({ id: 'test' })}</TableCell>
                                    <TableCell align="center">{formatMessage({ id: 'dirPractice' })}</TableCell>
                                    <TableCell align="center">{formatMessage({ id: 'dirZachet' })}</TableCell>
                                    <TableCell align="center">
                                        {formatMessage({ id: 'dirSurveyCol' })}
                                        <Box sx={{ textTransform: 'none', fontWeight: 500 }}>{month} {formatMessage({ id: 'month' }).toLowerCase()}</Box>
                                    </TableCell>
                                    {isWeek ? (
                                        <TableCell align="center">{formatMessage({ id: 'salaryWeekSummary' })}</TableCell>
                                    ) : (
                                        <>
                                            <TableCell align="right">{formatMessage({ id: 'colMonthTotal' })}</TableCell>
                                            <TableCell align="right">{formatMessage({ id: 'dirShare' })}</TableCell>
                                            <TableCell>{formatMessage({ id: 'approvalCol' })}</TableCell>
                                            <TableCell align="center">{formatMessage({ id: 'dirPaidCol' })}</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((m) => {
                                    const approved = m.approvalStatus === 'APPROVED'
                                    const weekRes = Math.round(((m.avgVideo ?? 0) + (m.avgTest ?? 0) + (m.avgPractice ?? 0) + (m.avgZachet ?? 0)) / 4)
                                    return (
                                        <TableRow
                                            key={`${m.mentorUserId}:${m.courseSubjectId}:${m.groupId ?? 0}`}
                                            hover
                                            sx={{ ...bodyRow, cursor: 'pointer' }}
                                            onClick={() => open(m)}
                                        >
                                            <TableCell>
                                                <b>{name(m)}</b>
                                                <Box sx={{ fontSize: '12px', color: ui.muted }}>{m.groupTitle || (locale === 'kz' ? m.subjectTitleKz : m.subjectTitleRu)}</Box>
                                            </TableCell>
                                            <TableCell align="right">{m.studentCount}</TableCell>
                                            <TableCell align="center"><PctPill value={m.avgVideo} good={90} warn={65} /></TableCell>
                                            <TableCell align="center"><PctPill value={m.avgTest} good={85} warn={60} /></TableCell>
                                            <TableCell align="center"><PctPill value={m.avgPractice} good={85} warn={50} /></TableCell>
                                            <TableCell align="center"><PctPill value={m.avgZachet} good={100} warn={75} /></TableCell>
                                            <TableCell align="center" onClick={stop}>
                                                <SurveyInput row={m} cycleNumber={month} />
                                            </TableCell>
                                            {isWeek ? (
                                                <TableCell align="center">
                                                    <b style={{ fontSize: 15 }}>{weekRes}%</b>
                                                    <Box sx={muted}>{formatMessage({ id: 'dirWeekIndicator' })}</Box>
                                                </TableCell>
                                            ) : (
                                                <>
                                                    <TableCell align="right"><b>{money(m.total)}</b></TableCell>
                                                    <TableCell align="right">{m.maxTotal ? Math.round((m.total * 100) / m.maxTotal) : 0}%</TableCell>
                                                    <TableCell onClick={stop}>
                                                        {approved ? (
                                                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <Pill tone="good" title={formatMessage({ id: 'dirApprovedHint' })}>
                                                                    🔒 {formatMessage({ id: 'approvalApproved' })}
                                                                </Pill>
                                                                {!m.paid && (
                                                                    <Button size="small" sx={{ ...btn, padding: '2px 8px', color: ui.sub }} disabled={deciding} onClick={() => act(m, 'UNAPPROVE')}>
                                                                        {formatMessage({ id: 'dirUnapprove' })}
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        ) : surveyEntered(m) ? (
                                                            <Button size="small" variant="contained" sx={{ ...btn, background: ui.pink, '&:hover': { background: ui.pinkDark } }} disabled={deciding} onClick={() => act(m, 'APPROVE')}>
                                                                {formatMessage({ id: 'approvalApprove' })}
                                                            </Button>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell align="center" onClick={stop}>
                                                        {approved && (
                                                            <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: '12.5px', color: tone.ok.text, fontWeight: 700 }}>
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!m.paid}
                                                                    disabled={deciding || (!!m.paid && !isSuper)}
                                                                    onChange={(e) => act(m, e.target.checked ? 'PAID' : 'UNPAID')}
                                                                />
                                                                {m.paid && `${formatMessage({ id: 'dirPaidShort' })} ${ddmm(m.paidAt)}`}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    )
                                })}
                                {!isFetching && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ color: ui.muted, padding: '28px' }}>
                                            {formatMessage({ id: 'salaryNoMentors' })}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!isWeek && rows.length > 0 && (
                                    <TableRow sx={{ ...bodyRow, background: ui.surface }}>
                                        <TableCell><b>{formatMessage({ id: 'practiceTotal' })}</b></TableCell>
                                        <TableCell align="right"><b>{rows.reduce((s, m) => s + (m.studentCount || 0), 0)}</b></TableCell>
                                        <TableCell colSpan={5} />
                                        <TableCell align="right"><b>{money(total)}</b></TableCell>
                                        <TableCell colSpan={3} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                    <Box sx={{ ...muted, marginTop: '8px' }}>{formatMessage({ id: 'dirSalaryRowHint' })}</Box>
                </>
            )}
        </>
    )
}

// Опрос % вводится прямо в строке; сохраняется по Enter или при уходе с поля
export const SurveyInput: React.FC<{ row: MentorSalaryRow; cycleNumber: number; wide?: boolean }> = ({ row, cycleNumber, wide }) => {
    const { formatMessage } = useIntl()
    const [save, { isLoading }] = directorApi.useSaveSurveyMutation()
    const current = surveyEntered(row) ? String(row.surveyPercent ?? 0) : ''
    const [value, setValue] = useState<string | null>(null)
    const shown = value ?? current

    const commit = async () => {
        if (value == null || value === current || value === '') {
            setValue(null)
            return
        }
        const pct = Math.max(0, Math.min(100, Math.round(Number(value) || 0)))
        try {
            await save({ mentorUserId: row.mentorUserId, courseSubjectId: row.courseSubjectId, cycleNumber, scorePercent: pct }).unwrap()
            toast.success(formatMessage({ id: 'salarySavedToast' }))
        } catch { /* общий обработчик */ }
        setValue(null)
    }

    return (
        <TextField
            size="small"
            type="number"
            value={shown}
            placeholder="—"
            disabled={!!row.paid || isLoading}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
            inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '6px', fontWeight: 700 }, 'aria-label': formatMessage({ id: 'dirSurveyCol' }) }}
            sx={{ width: wide ? 96 : 70 }}
        />
    )
}

const tabSx = (active: boolean) => ({
    padding: '8px 16px',
    borderRadius: ui.radius,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '13.5px',
    fontWeight: active ? 700 : 500,
    color: active ? ui.pink : ui.sub,
    backgroundColor: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
})

export default DirectorSalaryPage
